import router from '@/router'

export * from 'bootstrap-vue/esm/index'

const asArray = value => {
  if (!value) return []
  return Array.isArray(value) ? value : [value]
}

const getDefaultSlot = vm => {
  const slot = vm.$scopedSlots.default ? vm.$scopedSlots.default() : vm.$slots.default
  return asArray(slot)
}

const variantClasses = (prefix, variants) => Object.entries(variants)
  .filter(([, value]) => value)
  .map(([name, value]) => `${prefix ? `${prefix}-` : ''}${name}-${value}`)

const isModifiedEvent = event => event.metaKey || event.altKey || event.ctrlKey || event.shiftKey

const normalizeTo = to => {
  if (typeof to !== 'string') {
    return to
  }

  if (to.startsWith('/') || to.startsWith('?') || to.startsWith('#')) {
    return to
  }

  return { name: to }
}

export const BLink = {
  name: 'BLink',
  inheritAttrs: false,
  props: {
    to: {
      type: [String, Object],
      default: null,
    },
    href: {
      type: String,
      default: null,
    },
    target: {
      type: String,
      default: '_self',
    },
    rel: {
      type: String,
      default: null,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    active: {
      type: Boolean,
      default: false,
    },
    replace: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    resolvedTo() {
      return this.to ? normalizeTo(this.to) : null
    },
    resolvedHref() {
      if (this.resolvedTo) {
        return router.resolve(this.resolvedTo).href
      }

      return this.href
    },
  },
  methods: {
    onClick(event) {
      if (this.disabled) {
        event.preventDefault()
        event.stopPropagation()
        return
      }

      if (this.resolvedTo && this.target !== '_blank' && !isModifiedEvent(event) && event.button === 0) {
        event.preventDefault()
        const navigation = this.replace ? router.replace(this.resolvedTo) : router.push(this.resolvedTo)

        if (navigation && typeof navigation.catch === 'function') {
          navigation.catch(() => {})
        }
      }

      const clickHandlers = this.$listeners.click
      if (Array.isArray(clickHandlers)) {
        clickHandlers.forEach(handler => handler(event))
      } else if (clickHandlers) {
        clickHandlers(event)
      }
    },
  },
  render(h) {
    const listeners = {
      ...this.$listeners,
      click: this.onClick,
    }

    return h('a', {
      class: {
        active: this.active,
        disabled: this.disabled,
      },
      attrs: {
        ...this.$attrs,
        href: this.resolvedHref || null,
        rel: this.rel,
        target: this.target,
        tabindex: this.disabled ? '-1' : this.$attrs.tabindex || null,
        'aria-disabled': this.disabled ? 'true' : null,
      },
      on: listeners,
    }, getDefaultSlot(this))
  },
}

export const BCard = {
  name: 'BCard',
  inheritAttrs: false,
  props: {
    tag: {
      type: String,
      default: 'div',
    },
    title: {
      type: String,
      default: '',
    },
    subTitle: {
      type: String,
      default: '',
    },
    align: {
      type: String,
      default: '',
    },
    noBody: {
      type: Boolean,
      default: false,
    },
    bgVariant: {
      type: String,
      default: '',
    },
    borderVariant: {
      type: String,
      default: '',
    },
    textVariant: {
      type: String,
      default: '',
    },
  },
  render(h) {
    const slot = getDefaultSlot(this)
    const content = []

    if (this.title) {
      content.push(h('h4', { staticClass: 'card-title' }, this.title))
    }

    if (this.subTitle) {
      content.push(h('h6', { staticClass: 'card-subtitle text-muted' }, this.subTitle))
    }

    if (slot.length) {
      content.push(...slot)
    }

    const children = this.noBody
      ? content
      : [h('div', { staticClass: 'card-body' }, content)]

    return h(this.tag, {
      staticClass: 'card',
      class: [
        this.align ? `text-${this.align}` : null,
        ...variantClasses('', {
          bg: this.bgVariant,
          border: this.borderVariant,
          text: this.textVariant,
        }),
      ],
      attrs: this.$attrs,
      on: this.$listeners,
    }, children)
  },
}

export const BCardBody = {
  name: 'BCardBody',
  inheritAttrs: false,
  props: {
    bodyTag: {
      type: String,
      default: 'div',
    },
    bodyClass: {
      type: [String, Array, Object],
      default: null,
    },
    title: {
      type: String,
      default: '',
    },
    subTitle: {
      type: String,
      default: '',
    },
    overlay: {
      type: Boolean,
      default: false,
    },
    bodyBgVariant: {
      type: String,
      default: '',
    },
    bodyBorderVariant: {
      type: String,
      default: '',
    },
    bodyTextVariant: {
      type: String,
      default: '',
    },
  },
  render(h) {
    const children = []

    if (this.title) {
      children.push(h('h4', { staticClass: 'card-title' }, this.title))
    }

    if (this.subTitle) {
      children.push(h('h6', { staticClass: 'card-subtitle text-muted mb-2' }, this.subTitle))
    }

    children.push(...getDefaultSlot(this))

    return h(this.bodyTag, {
      staticClass: 'card-body',
      class: [
        this.bodyClass,
        {
          'card-img-overlay': this.overlay,
        },
        ...variantClasses('', {
          bg: this.bodyBgVariant,
          border: this.bodyBorderVariant,
          text: this.bodyTextVariant,
        }),
      ],
      attrs: this.$attrs,
      on: this.$listeners,
    }, children)
  },
}

export const BCardHeader = {
  name: 'BCardHeader',
  inheritAttrs: false,
  props: {
    headerTag: {
      type: String,
      default: 'div',
    },
    header: {
      type: String,
      default: '',
    },
    headerClass: {
      type: [String, Array, Object],
      default: null,
    },
    headerBgVariant: {
      type: String,
      default: '',
    },
    headerBorderVariant: {
      type: String,
      default: '',
    },
    headerTextVariant: {
      type: String,
      default: '',
    },
  },
  render(h) {
    const slot = getDefaultSlot(this)

    return h(this.headerTag, {
      staticClass: 'card-header',
      class: [
        this.headerClass,
        ...variantClasses('', {
          bg: this.headerBgVariant,
          border: this.headerBorderVariant,
          text: this.headerTextVariant,
        }),
      ],
      attrs: this.$attrs,
      on: this.$listeners,
    }, slot.length ? slot : this.header)
  },
}

export const BCardFooter = {
  name: 'BCardFooter',
  inheritAttrs: false,
  props: {
    footerTag: {
      type: String,
      default: 'div',
    },
    footer: {
      type: String,
      default: '',
    },
    footerClass: {
      type: [String, Array, Object],
      default: null,
    },
    footerBgVariant: {
      type: String,
      default: '',
    },
    footerBorderVariant: {
      type: String,
      default: '',
    },
    footerTextVariant: {
      type: String,
      default: '',
    },
  },
  render(h) {
    const slot = getDefaultSlot(this)

    return h(this.footerTag, {
      staticClass: 'card-footer',
      class: [
        this.footerClass,
        ...variantClasses('', {
          bg: this.footerBgVariant,
          border: this.footerBorderVariant,
          text: this.footerTextVariant,
        }),
      ],
      attrs: this.$attrs,
      on: this.$listeners,
    }, slot.length ? slot : this.footer)
  },
}

export const BCardTitle = {
  name: 'BCardTitle',
  inheritAttrs: false,
  props: {
    title: {
      type: String,
      default: '',
    },
    titleTag: {
      type: String,
      default: 'h4',
    },
  },
  render(h) {
    const slot = getDefaultSlot(this)

    return h(this.titleTag, {
      staticClass: 'card-title',
      attrs: this.$attrs,
      on: this.$listeners,
    }, slot.length ? slot : this.title)
  },
}

export const BCardSubTitle = {
  name: 'BCardSubTitle',
  inheritAttrs: false,
  props: {
    subTitle: {
      type: String,
      default: '',
    },
    subTitleTag: {
      type: String,
      default: 'h6',
    },
    subTitleTextVariant: {
      type: String,
      default: 'muted',
    },
  },
  render(h) {
    const slot = getDefaultSlot(this)

    return h(this.subTitleTag, {
      staticClass: 'card-subtitle',
      class: this.subTitleTextVariant ? `text-${this.subTitleTextVariant}` : null,
      attrs: this.$attrs,
      on: this.$listeners,
    }, slot.length ? slot : this.subTitle)
  },
}

export const BCardText = {
  name: 'BCardText',
  inheritAttrs: false,
  props: {
    textTag: {
      type: String,
      default: 'p',
    },
  },
  render(h) {
    return h(this.textTag, {
      staticClass: 'card-text',
      attrs: this.$attrs,
      on: this.$listeners,
    }, getDefaultSlot(this))
  },
}
